import React, { useState } from 'react';
import { Search, Package, Truck, Save, RotateCcw, CheckCircle2, AlertCircle, FileText, ChevronDown } from 'lucide-react';
// Mock Data
const SERVICES = [{
  id: 'weighbridge',
  name: 'Weighbridge Services'
}, {
  id: 'labor',
  name: 'Labor & Handling'
}, {
  id: 'equipment',
  name: 'Equipment Rental'
}, {
  id: 'storage',
  name: 'Special Storage'
}];
const CATEGORIES: Record<string, {
  id: string;
  name: string;
  rate: number;
}[]> = {
  weighbridge: [{
    id: 'single',
    name: 'Single Weighment',
    rate: 150
  }, {
    id: 'double',
    name: 'Double Weighment',
    rate: 250
  }],
  labor: [{
    id: 'loading',
    name: 'Loading Cargo',
    rate: 500
  }, {
    id: 'unloading',
    name: 'Unloading Cargo',
    rate: 500
  }, {
    id: 'shifting',
    name: 'Internal Shifting',
    rate: 300
  }],
  equipment: [{
    id: 'forklift',
    name: 'Forklift (Per Hour)',
    rate: 800
  }, {
    id: 'crane',
    name: 'Crane (Per Hour)',
    rate: 1500
  }, {
    id: 'reach_stacker',
    name: 'Reach Stacker (Per Move)',
    rate: 1200
  }],
  storage: [{
    id: 'covered',
    name: 'Covered Storage (Per Sq.Ft/Day)',
    rate: 10
  }, {
    id: 'open',
    name: 'Open Yard (Per Sq.Ft/Day)',
    rate: 5
  }, {
    id: 'cold',
    name: 'Cold Storage (Per CBM/Day)',
    rate: 25
  }]
};
interface GatepassData {
  gatepassNumber: string;
  vehicleNo: string;
  commodity: string;
  vehicleType: string;
  status: string;
  partyName: string;
}
export function OtherServicesDashboard() {
  const [searchQuery, setSearchQuery] = useState('');
  const [gatepassData, setGatepassData] = useState<GatepassData | null>(null);
  const [selectedService, setSelectedService] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success'>('idle');
  const [isSearching, setIsSearching] = useState(false);
  const handleSearch = () => {
    setErrors({});
    if (!searchQuery.trim()) {
      setErrors({
        search: 'Please enter a gatepass number'
      });
      return;
    }
    setIsSearching(true);
    setTimeout(() => {
      setGatepassData({
        gatepassNumber: searchQuery.toUpperCase(),
        vehicleNo: 'WB73G9558',
        commodity: 'ELECTRONICS',
        vehicleType: 'CONTAINER TRUCK',
        status: 'Inside Terminal',
        partyName: 'ABC Logistics Pvt Ltd'
      });
      setIsSearching(false);
      setSelectedService('');
      setSelectedCategory('');
    }, 600);
  };
  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedService(e.target.value);
    setSelectedCategory('');
    setErrors(prev => ({
      ...prev,
      service: '',
      category: ''
    }));
  };
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setErrors(prev => ({
      ...prev,
      category: ''
    }));
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!gatepassData) {
      newErrors.general = 'Please search and select a gatepass first';
    }
    if (!selectedService) {
      newErrors.service = 'Please select a service';
    }
    if (!selectedCategory) {
      newErrors.category = 'Please select a category';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (validateForm()) {
      setSaveStatus('success');
      setTimeout(() => {
        setSaveStatus('idle');
        handleReset();
      }, 3000);
    }
  };
  const handleReset = () => {
    setSearchQuery('');
    setGatepassData(null);
    setSelectedService('');
    setSelectedCategory('');
    setErrors({});
    setSaveStatus('idle');
  };
  const getSelectedCategoryData = () => {
    if (!selectedService || !selectedCategory) return null;
    return CATEGORIES[selectedService]?.find(c => c.id === selectedCategory);
  };
  const categoryData = getSelectedCategoryData();
  return <div className="flex flex-col gap-6 w-full h-full pb-24 max-w-5xl mx-auto" data-id="element-719">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-720">
        <div data-id="element-721">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-722">
            Other Services Billing
          </h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-723">
            Add additional services and charges to an existing gatepass.
          </p>
        </div>

        {saveStatus === 'success' && <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-200 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-724">
            <CheckCircle2 size={18} data-id="element-725" />
            <span className="font-medium text-sm" data-id="element-726">
              Service charges saved successfully!
            </span>
          </div>}
      </div>

      {errors.general && <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 p-3 rounded-lg text-sm font-medium" data-id="element-727">
          <AlertCircle size={18} data-id="element-728" />
          {errors.general}
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start" data-id="element-729">
        {/* LEFT COLUMN: Search & Gatepass Details */}
        <div className="lg:col-span-5 flex flex-col gap-6" data-id="element-730">
          {/* Search Card */}
          <div className={`bg-white p-6 rounded-xl shadow-sm border transition-all duration-300 ${gatepassData ? 'border-orange-200 ring-1 ring-orange-100' : 'border-gray-200'}`} data-id="element-731">
            <div className="flex items-center gap-3 mb-4" data-id="element-732">
              <div className="bg-orange-100 p-2 rounded-lg text-orange-600" data-id="element-733">
                <Search size={20} data-id="element-734" />
              </div>
              <h2 className="text-lg font-bold text-gray-800" data-id="element-735">Find Gatepass</h2>
            </div>

            <div className="flex flex-col gap-2" data-id="element-736">
              <div className="flex gap-2" data-id="element-737">
                <div className="relative flex-1" data-id="element-738">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-739" />
                  <input type="text" value={searchQuery} onChange={e => {
                  setSearchQuery(e.target.value);
                  if (errors.search) setErrors(prev => ({
                    ...prev,
                    search: ''
                  }));
                }} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Enter Gatepass No." className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all ${errors.search ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} data-id="element-740" />
                </div>
                <button onClick={handleSearch} disabled={isSearching} className="bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-70 flex items-center justify-center min-w-[80px]" data-id="element-741">
                  {isSearching ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-742" /> : 'Find'}
                </button>
              </div>
              {errors.search && <span className="text-xs text-red-500 font-medium ml-1" data-id="element-743">
                  {errors.search}
                </span>}
            </div>
          </div>

          {/* Gatepass Details Card */}
          {gatepassData && <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-744">
              <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-100" data-id="element-745">
                <div data-id="element-746">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-747">
                    Gatepass Number
                  </span>
                  <p className="text-lg font-mono font-bold text-gray-900 mt-1" data-id="element-748">
                    {gatepassData.gatepassNumber}
                  </p>
                </div>
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full border border-green-200" data-id="element-749">
                  {gatepassData.status}
                </span>
              </div>

              <div className="space-y-4" data-id="element-750">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3" data-id="element-751">
                  <div className="bg-white p-2 rounded shadow-sm border border-gray-100" data-id="element-752">
                    <Truck size={16} className="text-gray-600" data-id="element-753" />
                  </div>
                  <div data-id="element-754">
                    <span className="text-[10px] font-bold text-gray-400 uppercase block" data-id="element-755">
                      Vehicle No
                    </span>
                    <p className="font-bold text-gray-800 text-sm" data-id="element-756">
                      {gatepassData.vehicleNo}
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3" data-id="element-757">
                  <div className="bg-white p-2 rounded shadow-sm border border-gray-100" data-id="element-758">
                    <Package size={16} className="text-gray-600" data-id="element-759" />
                  </div>
                  <div data-id="element-760">
                    <span className="text-[10px] font-bold text-gray-400 uppercase block" data-id="element-761">
                      Commodity
                    </span>
                    <p className="font-bold text-gray-800 text-sm" data-id="element-762">
                      {gatepassData.commodity}
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3" data-id="element-763">
                  <div className="bg-white p-2 rounded shadow-sm border border-gray-100" data-id="element-764">
                    <FileText size={16} className="text-gray-600" data-id="element-765" />
                  </div>
                  <div data-id="element-766">
                    <span className="text-[10px] font-bold text-gray-400 uppercase block" data-id="element-767">
                      Party Name
                    </span>
                    <p className="font-bold text-gray-800 text-sm" data-id="element-768">
                      {gatepassData.partyName}
                    </p>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* RIGHT COLUMN: Service Selection Form */}
        <div className={`lg:col-span-7 transition-opacity duration-500 ${gatepassData ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-769">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-770">
            <h2 className="text-lg font-bold text-gray-800 mb-6 pb-4 border-b border-gray-100" data-id="element-771">
              Service Details
            </h2>

            <div className="space-y-6" data-id="element-772">
              {/* Service Dropdown */}
              <div data-id="element-773">
                <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-774">
                  Select Service <span className="text-red-500" data-id="element-775">*</span>
                </label>
                <div className="relative" data-id="element-776">
                  <select value={selectedService} onChange={handleServiceChange} className={`w-full appearance-none px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all bg-white font-medium text-gray-700 ${errors.service ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} data-id="element-777">
                    <option value="" data-id="element-778">-- Select a Service --</option>
                    {SERVICES.map(service => <option key={service.id} value={service.id} data-id="element-779">
                        {service.name}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} data-id="element-780" />
                </div>
                {errors.service && <span className="text-xs text-red-500 font-medium mt-1 block" data-id="element-781">
                    {errors.service}
                  </span>}
              </div>

              {/* Category Dropdown (Visible only if service is selected) */}
              {selectedService && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-782">
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-2" data-id="element-783">
                    Service Category <span className="text-red-500" data-id="element-784">*</span>
                  </label>
                  <div className="relative" data-id="element-785">
                    <select value={selectedCategory} onChange={handleCategoryChange} className={`w-full appearance-none px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all bg-white font-medium text-gray-700 ${errors.category ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} data-id="element-786">
                      <option value="" data-id="element-787">-- Select Category --</option>
                      {CATEGORIES[selectedService]?.map(category => <option key={category.id} value={category.id} data-id="element-788">
                          {category.name} - NPR {category.rate}
                        </option>)}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} data-id="element-789" />
                  </div>
                  {errors.category && <span className="text-xs text-red-500 font-medium mt-1 block" data-id="element-790">
                      {errors.category}
                    </span>}
                </div>}

              {/* Total Amount (shown when category is selected) */}
              {selectedCategory && categoryData && <div className="flex items-center justify-between pt-4 border-t border-gray-100 animate-in fade-in duration-300" data-id="element-791">
                  <span className="text-sm font-bold text-gray-700" data-id="element-792">
                    Total Amount
                  </span>
                  <span className="text-xl font-bold text-orange-600 font-mono" data-id="element-793">
                    NPR {categoryData.rate.toLocaleString()}
                  </span>
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10 lg:pl-72" data-id="element-794">
        <div className="max-w-5xl mx-auto flex justify-between items-center" data-id="element-795">
          <button onClick={handleReset} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-796">
            <RotateCcw size={18} data-id="element-797" />
            Clear Form
          </button>

          <button onClick={handleSave} disabled={!gatepassData || !selectedService || !selectedCategory} className={`flex items-center gap-2 px-8 py-2.5 rounded-lg font-bold text-white shadow-md transition-all ${gatepassData && selectedService && selectedCategory ? 'bg-orange-500 hover:bg-orange-600 hover:shadow-lg hover:-translate-y-0.5' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-798">
            <Save size={18} data-id="element-799" />
            Save Service Charges
          </button>
        </div>
      </div>
    </div>;
}